import type { Destination, ImageAsset } from "../types";

const localImage = (
  slug: string,
  alt: string,
  focalPoint = "center",
  credit = "Unsplash",
): ImageAsset => ({
  src: `/destinations/${slug}.webp`,
  alt,
  width: 1600,
  height: 1067,
  credit,
  focalPoint,
});

const beach = localImage(
  "boracay",
  "Boracay White Beach with powder-white sand, clear water, and a blue paraw sailboat",
);
const islandLagoon = localImage(
  "palawan",
  "Turquoise lagoon surrounded by limestone karst cliffs in El Nido, Palawan",
);
const greenHills = localImage(
  "batanes",
  "Rolling green coastal hills and the blue Philippine Sea in Batanes",
  "center",
  "Cid Jacobo · CC BY-SA 4.0 · Wikimedia Commons",
);
const surf = localImage(
  "siargao",
  "Aerial view of Siargao's reef, tropical shoreline, and clear blue water",
);
const tokyo = localImage(
  "japan",
  "Colorful illuminated city crossing in Tokyo, Japan",
);
const seoul = localImage(
  "south-korea",
  "Traditional Korean palace pavilion beside a pond in Seoul",
);
const vietnam = localImage(
  "vietnam",
  "Limestone islands and calm water in Ha Long Bay, Vietnam",
);
const bangkok = localImage(
  "thailand",
  "Tuk-tuks and glowing night signs on a lively Bangkok street",
);
const singapore = localImage(
  "singapore",
  "Marina Bay Sands and the waterfront skyline in Singapore",
);

export const destinations = [
  {
    id: "dest-boracay",
    slug: "boracay",
    name: "Boracay",
    country: "Philippines",
    region: "Western Visayas",
    category: "domestic",
    shortDescription: "Powder-soft shores, golden sunsets, and easy island days.",
    description:
      "Unwind along Boracay’s celebrated White Beach, then balance slow mornings with island-hopping, water activities, and lively sunset dining. ACES can shape the pace around couples, families, or barkada trips.",
    image: beach,
    heroImage: beach,
    gallery: [beach, islandLagoon, surf],
    startingPrice: { amount: 12_990, currency: "PHP", qualifier: "per-person" },
    packageCount: 3,
    highlights: ["White Beach", "Island hopping", "Sunset sailing", "Local food stops"],
    bestTimeToVisit: "November to May for generally drier days",
    travelTime: "Around 1 hour by air from Manila, plus land and boat transfers",
    featured: true,
  },
  {
    id: "dest-batanes",
    slug: "batanes",
    name: "Batanes",
    country: "Philippines",
    region: "Cagayan Valley",
    category: "domestic",
    shortDescription: "Wind-swept hills, stone villages, and quiet northern beauty.",
    description:
      "Explore Batanes at a thoughtful pace through rolling pastureland, dramatic coastlines, heritage homes, and warm Ivatan hospitality. Weather changes quickly, so flexible planning is part of the experience.",
    image: greenHills,
    heroImage: greenHills,
    gallery: [greenHills, beach, islandLagoon],
    startingPrice: { amount: 24_900, currency: "PHP", qualifier: "per-person" },
    packageCount: 1,
    highlights: ["North Batan tour", "South Batan tour", "Ivatan heritage", "Sabtang option"],
    bestTimeToVisit: "March to June, while still planning around local weather",
    travelTime: "Around 1 hour 45 minutes by direct flight from Manila",
    featured: true,
  },
  {
    id: "dest-palawan",
    slug: "palawan",
    name: "Palawan",
    country: "Philippines",
    region: "Mimaropa",
    category: "domestic",
    shortDescription: "Limestone lagoons, secret beaches, and unforgettable blue water.",
    description:
      "Choose between El Nido’s dramatic lagoons, Coron’s reefs and lakes, or Puerto Princesa’s nature-led escapes. ACES can combine flights, stays, transfers, and guided island experiences.",
    image: islandLagoon,
    heroImage: islandLagoon,
    gallery: [islandLagoon, beach, surf],
    startingPrice: { amount: 16_500, currency: "PHP", qualifier: "per-person" },
    packageCount: 3,
    highlights: ["El Nido lagoons", "Coron reefs", "Island hopping", "Beachfront stays"],
    bestTimeToVisit: "November to May for calmer sea conditions",
    travelTime: "About 1–1.5 hours by air from Manila, depending on gateway",
    featured: true,
  },
  {
    id: "dest-siargao",
    slug: "siargao",
    name: "Siargao",
    country: "Philippines",
    region: "Caraga",
    category: "domestic",
    shortDescription: "Surf energy, palm-lined roads, and laid-back island living.",
    description:
      "Go beyond Cloud 9 with lagoons, island-hopping, mangrove views, cafés, and relaxed beach evenings. Itineraries can be active, unhurried, or a little of both.",
    image: surf,
    heroImage: surf,
    gallery: [surf, beach, islandLagoon],
    startingPrice: { amount: 17_800, currency: "PHP", qualifier: "per-person" },
    packageCount: 2,
    highlights: ["Cloud 9", "Sugba Lagoon", "Three-island tour", "Coconut road"],
    bestTimeToVisit: "March to October; surf conditions vary by season",
    travelTime: "About 2–2.5 hours by direct flight from Manila",
    featured: true,
  },
  {
    id: "dest-japan",
    slug: "japan",
    name: "Japan",
    country: "Japan",
    region: "East Asia",
    category: "international",
    shortDescription: "Old-world rituals, future-facing cities, and seasonal wonder.",
    description:
      "Discover Tokyo’s energy, Kyoto’s heritage, Osaka’s food scene, or a carefully timed seasonal escape. We can help organize flight, hotel, tours, and visa-document guidance for Filipino travelers.",
    image: tokyo,
    heroImage: tokyo,
    gallery: [tokyo, greenHills, seoul],
    startingPrice: { amount: 48_900, currency: "PHP", qualifier: "per-person" },
    packageCount: 3,
    highlights: ["Tokyo city days", "Kyoto heritage", "Osaka food stops", "Seasonal itineraries"],
    bestTimeToVisit: "Year-round; spring and autumn are especially popular",
    travelTime: "Around 4–5 hours by direct flight from Manila",
    featured: true,
  },
  {
    id: "dest-south-korea",
    slug: "south-korea",
    name: "South Korea",
    country: "South Korea",
    region: "East Asia",
    category: "international",
    shortDescription: "Palaces, cafés, mountain color, and modern Seoul style.",
    description:
      "Pair Seoul’s neighborhoods and royal palaces with day trips, seasonal scenery, and food experiences. Our inquiry team can help align an itinerary with your preferred pace and priorities.",
    image: seoul,
    heroImage: seoul,
    gallery: [seoul, tokyo, greenHills],
    startingPrice: { amount: 39_900, currency: "PHP", qualifier: "per-person" },
    packageCount: 2,
    highlights: ["Seoul palaces", "Nami Island", "Market food", "Seasonal scenery"],
    bestTimeToVisit: "April to June and September to November",
    travelTime: "Around 4 hours by direct flight from Manila",
    featured: true,
  },
  {
    id: "dest-vietnam",
    slug: "vietnam",
    name: "Vietnam",
    country: "Vietnam",
    region: "Southeast Asia",
    category: "international",
    shortDescription: "Layered history, bold flavors, and cinematic landscapes.",
    description:
      "Combine energetic city streets with heritage quarters, limestone bays, mountain scenery, or the lantern-lit charm of central Vietnam. Packages can be customized around food, culture, or nature.",
    image: vietnam,
    heroImage: vietnam,
    gallery: [vietnam, bangkok, islandLagoon],
    startingPrice: { amount: 29_800, currency: "PHP", qualifier: "per-person" },
    packageCount: 2,
    highlights: ["Hanoi Old Quarter", "Ha Long Bay", "Hoi An", "Local food tours"],
    bestTimeToVisit: "Varies by region; February to April is a flexible starting point",
    travelTime: "Around 2.5–3.5 hours by direct flight from Manila",
    featured: false,
  },
  {
    id: "dest-thailand",
    slug: "thailand",
    name: "Thailand",
    country: "Thailand",
    region: "Southeast Asia",
    category: "international",
    shortDescription: "Temple mornings, night markets, and tropical escapes.",
    description:
      "Enjoy Bangkok’s contrasts, add heritage-rich day trips, or continue to a beach destination. ACES can help arrange a practical first-time itinerary or a more personalized return trip.",
    image: bangkok,
    heroImage: bangkok,
    gallery: [bangkok, beach, singapore],
    startingPrice: { amount: 27_500, currency: "PHP", qualifier: "per-person" },
    packageCount: 2,
    highlights: ["Bangkok temples", "Night markets", "Ayutthaya", "Island extensions"],
    bestTimeToVisit: "November to February for generally cooler weather",
    travelTime: "Around 3.5 hours by direct flight from Manila",
    featured: false,
  },
  {
    id: "dest-singapore",
    slug: "singapore",
    name: "Singapore",
    country: "Singapore",
    region: "Southeast Asia",
    category: "international",
    shortDescription: "A polished city break packed with food, gardens, and family fun.",
    description:
      "Singapore is easy to navigate and rich in contrasts—from hawker centers and heritage districts to waterfront landmarks and world-class attractions. It works beautifully for first international trips and family breaks.",
    image: singapore,
    heroImage: singapore,
    gallery: [singapore, tokyo, bangkok],
    startingPrice: { amount: 31_900, currency: "PHP", qualifier: "per-person" },
    packageCount: 2,
    highlights: ["Marina Bay", "Gardens by the Bay", "Sentosa", "Hawker culture"],
    bestTimeToVisit: "Year-round, with rain possible in any month",
    travelTime: "Around 3.5–4 hours by direct flight from Manila",
    featured: true,
  },
] satisfies Destination[];
